/**
 * EndpointControlList - Reusable sorted, filtered, grouped endpoint control list.
 *
 * Used by NodeInspector (full accordion with expand/config) and
 * TweakablePanel (compact controls only).
 */

import { useState, useCallback, useRef, useEffect, type CSSProperties } from 'react';
import { getEndpointColor, sortEndpoints, groupEndpoints } from '../../shared/utils';
import { EndpointControl } from './EndpointControl';
import { EndpointConfig } from './EndpointConfig';
import { ArraySliderControl } from '../../shared/valueControls/ArraySliderControl';
import { Tooltip } from '@/ui/shared/Tooltip';
import type { EndpointState } from '@/nodes/Endpoint';
import { useRuntime } from '@/ui/contexts/RuntimeProvider';

export interface EndpointControlItem {
  instanceId: string;
  nodeId: string;
  endpoint: EndpointState;
}

interface EndpointControlListProps {
  items: EndpointControlItem[];
  dataFlowFilter?: string[];
  /** Node type string — needed for EndpointConfig. Omit for compact mode. */
  nodeType?: string;
  /** Show accordion expand with EndpointConfig. Default false (compact mode). */
  expandable?: boolean;
  /** Initial endpoint id to expand and scroll to */
  initialExpandedId?: string;
  /** Callback when a custom param delete button is clicked */
  onDeleteCustomParam?: (item: EndpointControlItem) => void;
}

export function EndpointControlList({
  items,
  dataFlowFilter,
  nodeType,
  expandable = false,
  initialExpandedId,
  onDeleteCustomParam,
}: EndpointControlListProps) {
  const [expandedEndpoints, setExpandedEndpoints] = useState<Set<string>>(
    () => new Set(initialExpandedId ? [initialExpandedId] : [])
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const endpointRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggleEndpoint = useCallback((epId: string) => {
    setExpandedEndpoints(prev => {
      const next = new Set(prev);
      if (next.has(epId)) next.delete(epId);
      else next.add(epId);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  }, []);

  // Auto-scroll to initial expanded endpoint
  useEffect(() => {
    if (initialExpandedId && endpointRefs.current[initialExpandedId]) {
      setTimeout(() => {
        endpointRefs.current[initialExpandedId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 50);
    }
  }, [initialExpandedId]);

  // Build sorted [id, endpoint] pairs, apply dataFlow filter, then group
  const entries: [string, EndpointState][] = items.map(item => [item.endpoint.id, item.endpoint]);
  const sorted = sortEndpoints(entries);
  const filtered = dataFlowFilter
    ? sorted.filter(([, ep]) => dataFlowFilter.includes(ep.dataFlow))
    : sorted;
  const grouped = groupEndpoints(filtered);

  // Build a lookup from endpoint id → item (for instanceId/nodeId)
  const itemByEndpointId = new Map<string, EndpointControlItem>();
  for (const item of items) {
    itemByEndpointId.set(item.endpoint.id, item);
  }

  return (
    <>
      {grouped.map((entry, index) => {
        if (entry.kind === 'group') {
          const isCollapsed = !expandedGroups.has(entry.name);
          return (
            <div
              key={`group-${entry.name}`}
              onClick={() => toggleGroup(entry.name)}
              style={groupHeaderStyle}
            >
              <span style={{
                display: 'inline-block',
                transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s ease'
              }}>▼</span>
              {entry.name}
            </div>
          );
        }

        const item = itemByEndpointId.get(entry.name);
        if (!item) return null;

        // Determine if this endpoint's group is collapsed
        let currentGroup: string | undefined;
        for (let i = index - 1; i >= 0; i--) {
          const prev = grouped[i];
          if (prev.kind === 'group') { currentGroup = prev.name; break; }
        }
        const isCollapsed = currentGroup && !expandedGroups.has(currentGroup);

        if (expandable) {
          const isExpanded = expandedEndpoints.has(entry.name);
          return (
            <div
              key={entry.name}
              ref={el => { endpointRefs.current[entry.name] = el; }}
              style={{
                overflow: 'hidden',
                maxHeight: isCollapsed ? 0 : 500,
                transition: 'max-height 0.15s ease'
              }}
            >
              <AccordionItem
                endpoint={entry.endpoint}
                nodeId={item.nodeId}
                nodeType={nodeType ?? ''}
                instanceId={item.instanceId}
                isExpanded={isExpanded}
                onToggle={() => toggleEndpoint(entry.name)}
                onDelete={entry.endpoint.custom && onDeleteCustomParam
                  ? () => onDeleteCustomParam(item)
                  : undefined}
              />
            </div>
          );
        }

        // Compact mode: label + control on one row
        return (
          <div
            key={entry.name}
            style={{
              overflow: 'hidden',
              maxHeight: isCollapsed ? 0 : 200,
              transition: 'max-height 0.15s ease'
            }}
          >
            <CompactRow item={item} />
          </div>
        );
      })}
    </>
  );
}

// ── Compact row (performance panel) ──

function CompactRow({ item }: { item: EndpointControlItem }) {
  const { endpoint, instanceId, nodeId } = item;
  const { getValue, tweakValue } = useRuntime();
  const isArraySlider = endpoint.dataType === 'array' && endpoint.min !== undefined && endpoint.max !== undefined && endpoint.length;
  const stacked = endpoint.dataType === 'array' || endpoint.dataType === 'string' || endpoint.dataType === 'number';

  return (
    <div style={stacked ? compactRowStackedStyle : compactRowStyle}>
      <div style={compactLabelRowStyle}>
        <span style={compactLabelStyle}>{endpoint.label || endpoint.id}</span>
        {endpoint.info && (
          <Tooltip text={endpoint.info} placement="right">
            <span style={infoButtonStyle}>?</span>
          </Tooltip>
        )}
      </div>
      <div className="perf-control-full" style={stacked ? compactControlStackedStyle : compactControlStyle}>
        {isArraySlider ? (
          <ArraySliderControl
            value={getValue(instanceId, nodeId, endpoint.id) ?? []}
            length={endpoint.length!}
            min={endpoint.min}
            max={endpoint.max}
            step={endpoint.step}
            color={getEndpointColor(endpoint.dataType, endpoint.dataFlow)}
            onChange={(val) => tweakValue(instanceId, nodeId, endpoint.id, val)}
          />
        ) : (
          <EndpointControl
            instanceId={instanceId}
            nodeId={nodeId}
            endpoint={endpoint}
          />
        )}
      </div>
    </div>
  );
}

// ── Accordion item (NodeInspector) ──

interface AccordionItemProps {
  endpoint: EndpointState;
  nodeId: string;
  nodeType: string;
  instanceId: string;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete?: () => void;
}

function AccordionItem({ endpoint, nodeId, nodeType, instanceId, isExpanded, onToggle, onDelete }: AccordionItemProps) {
  const { getValue, tweakValue } = useRuntime();
  const isArraySlider = endpoint.dataType === 'array' && endpoint.min !== undefined && endpoint.max !== undefined && endpoint.length;

  return (
    <div style={accordionStyles.item}>
      <button onClick={onToggle} style={accordionStyles.header}>
        <span style={accordionStyles.arrow}>{isExpanded ? '▼' : '▶'}</span>
        <span style={accordionStyles.endpointName}>{endpoint.label || endpoint.id}</span>
        <span style={accordionStyles.direction}>
          {endpoint.isInput && !endpoint.isOutput && '←'}
          {endpoint.isOutput && !endpoint.isInput && '→'}
          {endpoint.isInput && endpoint.isOutput && '↔'}
        </span>
        <span style={{
          ...accordionStyles.type,
          background: getEndpointColor(endpoint.dataType, endpoint.dataFlow),
          color: 'white'
        }}>{endpoint.dataType ?? 'any'}</span>
        {onDelete && (
          <span
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            style={accordionStyles.deleteButton}
            title="Delete custom param"
          >
            ×
          </span>
        )}
      </button>

      {isExpanded && (
        <div style={accordionStyles.content}>
          <div style={{ marginBottom: 12 }}>
            {isArraySlider ? (
              <ArraySliderControl
                value={getValue(instanceId, nodeId, endpoint.id) ?? []}
                length={endpoint.length!}
                min={endpoint.min}
                max={endpoint.max}
                step={endpoint.step}
                color={getEndpointColor(endpoint.dataType, endpoint.dataFlow)}
                onChange={(val) => tweakValue(instanceId, nodeId, endpoint.id, val)}
              />
            ) : (
              <EndpointControl
                instanceId={instanceId}
                nodeId={nodeId}
                endpoint={endpoint}
              />
            )}
          </div>
          <EndpointConfig
            instanceId={instanceId}
            endpoint={endpoint}
            nodeId={nodeId}
            nodeType={nodeType}
          />
        </div>
      )}
    </div>
  );
}

// ── Styles ──

const groupHeaderStyle: React.CSSProperties = {
  padding: '8px 12px 4px',
  color: '#9ca3af',
  fontSize: 11,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderTop: '1px solid #374151',
  marginTop: 8,
  cursor: 'pointer',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 4
};

const compactRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  overflow: 'hidden',
};

const compactLabelRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
 
};

const compactLabelStyle: CSSProperties = {
  fontSize: 11,
  color: '#d1d5db',
   flexGrow: 1,
  whiteSpace: 'nowrap',
};

const infoButtonStyle: CSSProperties = {
  fontSize: 9,
  color: '#6b7280',
  cursor: 'pointer',
  width: 14,
  height: 14,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  border: '1px solid #4b5563',
  flexShrink: 0,
  lineHeight: 1,
  userSelect: 'none',
  marginLeft: 'auto',
};

const compactControlStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
};

const compactRowStackedStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '6px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const compactControlStackedStyle: React.CSSProperties = {
  width: '100%',
};

const accordionStyles: Record<string, React.CSSProperties> = {
  item: {
    borderBottom: '1px solid #374151'
  },
  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: 'transparent',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: 13
  },
  arrow: {
    fontSize: 10,
    color: '#6b7280',
    width: 12
  },
  endpointName: {
    fontWeight: 500,
    flex: 1
  },
  direction: {
    fontSize: 10,
    color: '#6b7280',
    width: 14
  },
  type: {
    fontSize: 10,
    color: '#9ca3af',
    background: '#374151',
    padding: '2px 6px',
    borderRadius: 3
  },
  deleteButton: {
    marginLeft: 4,
    color: '#6b7280',
    fontSize: 16,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 4px'
  },
  content: {
    padding: '8px 20px 16px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
};
